import type { PropsWithChildren, FunctionComponent } from 'react'
import { useMemo, useState } from 'react'
// eslint-disable-next-line @retailer-platform/no-restricted-imports
import { Popover } from '@mantine/core'
import styled from '@emotion/styled'
import cloneDeep from 'lodash/cloneDeep'
import { colors } from '@retailer-platform/shared-components'
import {
  TextInputLabel,
  type TextInputLabelProps,
} from '@retailer-platform/shared-components/src/tds'
import { useDashMessages } from '../../../intl/intl.hooks'
import { type ViewType } from '../retailer-scope-wrapper/ScopeSelection.types'
import { useScopeSelectorContext } from '../retailer-scope-wrapper/ScopeSelectorContext'
import { Divider } from './Common'
import PickerLabel from './PickerLabel'
import { getKey, getLeafNodes } from './utils'
import { type Option } from './utils'
import MultiScopeDropdownOptionContainer from './MultiScopeDropdownOptionContainer'
import ApplyCancelComponent from './ApplyCancelComponent'
import { type PopoverConfig } from './Common'
import { PopoverTriggerDiv } from './PopoverTrigger'

const RootContainer = styled.div<{ margin: string }>(({ margin }) => ({
  margin: margin || '10px 0px',
}))

const TriggerContainer = styled.div<{ width: string | number }>(({ width }) => ({
  width: width,
}))

export interface Props {
  options: Option[]
  multiSelect: boolean
  scopeSelectionOptional?: boolean
  openByDefault?: boolean
  onSingleSelect: (option: Option) => void
  onMultiSelect: (options: Option[]) => void
  selectionType: ViewType
  popoverConfig?: PopoverConfig
  defaultToAll?: boolean
  searchFilter: string
  setSearchFilter: (searchFilter: string) => void
  formLabel?: TextInputLabelProps
  nonInteractiveOptionsStringSet: Set<string>
}

const Container = styled.div({})

const OverflowContainer = styled.div<{ maxHeight: number }>(({ maxHeight }) => ({
  maxHeight: maxHeight,
  overflow: 'auto',
  padding: '8px 0px',
}))

const NoResults = styled.div({
  padding: '8px 16px',
  margin: '8px 0px',
  color: colors.GRAYSCALE.X80,
})

const renderOptions = (
  options: Option[],
  multiSelect: boolean,
  selectedState: Record<string, Option>,
  setSelectedState: (state: Record<string, Option>) => void,
  onSingleSelect: (option: Option) => void,
  searchFilter: string,
  nonInteractiveOptionsStringSet: Set<string>,
  selectionType: ViewType
) =>
  options.map(option => (
    <div key={getKey(option)}>
      <MultiScopeDropdownOptionContainer
        option={option}
        multiSelect={multiSelect}
        selectedState={selectedState}
        setSelectedState={setSelectedState}
        onSingleSelect={onSingleSelect}
        searchFilter={searchFilter}
        nonInteractiveOptionsStringSet={nonInteractiveOptionsStringSet}
        selectionType={selectionType}
      />
    </div>
  ))

const nameMatch = (option: Option, searchFilter: string) => {
  const lowerSearchFilter = searchFilter.toLowerCase()
  return (
    option.name.toLowerCase().includes(lowerSearchFilter) ||
    option.siteUrl?.toLowerCase()?.includes(lowerSearchFilter)
  )
}

const getFilteredOptions = (options: Option[], searchFilter: string) => {
  options.forEach(option => {
    if (!option.leafNode && option.children) {
      // what we're doing here is filtering the children options ONLY if the group
      // name does not match the search filter. If the group name matches the search
      // then we show all children options
      if (!nameMatch(option, searchFilter)) {
        option.children = getFilteredOptions(option.children, searchFilter)
      }
    }
  })

  const filteredOptions = options.filter(
    option => option.children?.length > 0 || nameMatch(option, searchFilter)
  )

  return filteredOptions
}

const MultiScopeDropdownComponent: FunctionComponent<PropsWithChildren<Props>> = ({
  options,
  multiSelect,
  scopeSelectionOptional,
  openByDefault,
  onSingleSelect,
  onMultiSelect,
  selectionType,
  popoverConfig,
  defaultToAll = true,
  searchFilter,
  setSearchFilter,
  formLabel,
  nonInteractiveOptionsStringSet,
}) => {
  const intlMessages = useDashMessages({
    noResults: 'multiScopePicker.noResults',
  })
  const [isOpen, setIsOpen] = useState(openByDefault)
  const { margin } = useScopeSelectorContext() || {}

  const setIsOpenInternal = (isOpen: boolean) => {
    setIsOpen(isOpen)

    if (!isOpen) {
      // reset the search filter when the dropdown is closed
      setSearchFilter('')
    }
  }

  const filteredOptions = useMemo(
    () => getFilteredOptions(cloneDeep(options), searchFilter),
    [options, searchFilter]
  )

  const leafNodes = getLeafNodes(filteredOptions)
  const defaultSelectedState: Record<string, Option> = {}

  leafNodes.forEach(node => {
    if (node.selected) {
      defaultSelectedState[getKey(node)] = node
    }
  })

  const [selectedState, setSelectedState] = useState<Record<string, Option>>(defaultSelectedState)
  const [tempSelectedState, setTempSelectedState] =
    useState<Record<string, Option>>(defaultSelectedState)

  const onClickAccept = () => {
    setIsOpenInternal(false)
    setTempSelectedState(selectedState)
    if (Object.values(selectedState).length > 0) {
      onMultiSelect(Object.values(selectedState).filter(e => e.leafNode))
    } else if (defaultToAll) {
      // Only select all if defaultToAll is true
      // nothing has been selected, which impllies that all options should be selected
      const leafNodes = getLeafNodes(options)
      onMultiSelect(leafNodes)
    } else {
      onMultiSelect([]) // Otherwise return empty selection
    }
  }

  const onClear = () => {
    if (multiSelect) {
      if (defaultToAll) {
        // Only select all if defaultToAll is true
        // nothing has been selected, which impllies that all options should be selected
        const leafNodes = getLeafNodes(options)
        onMultiSelect(leafNodes)
      } else {
        onMultiSelect([]) // Otherwise return empty selection
      }
    } else {
      onSingleSelect(null)
    }
  }

  const onClickCancel = () => {
    setSelectedState(tempSelectedState)
    setIsOpenInternal(false)
  }

  const handlePopoverChange = () => {
    if (isOpen) {
      if (multiSelect) {
        setSelectedState(tempSelectedState)
      }
      setIsOpenInternal(false)
    }
  }

  const onTriggerClick = () => {
    if (isOpen) {
      handlePopoverChange()
    } else {
      setIsOpenInternal(true)
    }
  }

  const onSingleSelectInternal = (option: Option) => {
    setIsOpenInternal(false)
    onSingleSelect(option)
  }

  const height = popoverConfig?.maxHeight ?? 350
  const position = popoverConfig?.position ?? 'bottom-start'
  const offset = popoverConfig?.offset ?? 4
  const width = popoverConfig?.width ?? 450
  const withinPortal = popoverConfig?.withinPortal ?? true
  const triggerWidth = popoverConfig?.triggerWidth ?? 'auto'
  return (
    <RootContainer margin={margin}>
      {formLabel && <TextInputLabel marginBottom={2} {...formLabel} />}
      <Popover
        opened={isOpen}
        onChange={handlePopoverChange}
        position={position}
        offset={offset}
        zIndex={100}
        withinPortal={withinPortal}
        width={width}
      >
        <Popover.Target>
          <TriggerContainer width={triggerWidth}>
            <PopoverTriggerDiv
              onClick={onTriggerClick}
              isOpen={isOpen}
              data-testid="multi-scope-picker-trigger"
            >
              <PickerLabel
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                multiSelect={multiSelect}
                onClear={onClear}
                selectionType={selectionType}
                scopeSelectionOptional={scopeSelectionOptional}
                defaultToAll={defaultToAll}
                setTempSelectedState={setTempSelectedState}
                isOpen={isOpen}
                searchFilter={searchFilter}
                setSearchFilter={setSearchFilter}
              ></PickerLabel>
            </PopoverTriggerDiv>
          </TriggerContainer>
        </Popover.Target>
        <Popover.Dropdown style={{ padding: '0px' }}>
          <Container data-testid="multi-scope-dropdown-container">
            {filteredOptions.length > 0 ? (
              <>
                <OverflowContainer maxHeight={height}>
                  {renderOptions(
                    filteredOptions,
                    multiSelect,
                    selectedState,
                    setSelectedState,
                    onSingleSelectInternal,
                    searchFilter,
                    nonInteractiveOptionsStringSet,
                    selectionType
                  )}
                </OverflowContainer>

                {multiSelect && (
                  <>
                    <Divider />
                    <ApplyCancelComponent
                      onClickAccept={onClickAccept}
                      onClickCancel={onClickCancel}
                    />
                  </>
                )}
              </>
            ) : (
              <NoResults>{intlMessages.noResults}</NoResults>
            )}
          </Container>
        </Popover.Dropdown>
      </Popover>
    </RootContainer>
  )
}

export default MultiScopeDropdownComponent
